import { Injectable } from '@angular/core';
import {
  SupportingDocumentGridConfig,
  SupportingDocumentGridOption
} from './supporting-document-grid-config.model';
import { PageBuilderAddURL } from '@app/pages/page-builder/page-builder/page-builder-add/@core/page-builder-add-url.config';

@Injectable({
  providedIn: 'root'
})
export class SupportingDocumentGridConfigService {
  parse(rawConfig: any, fallbacks: { readOnlyExpression?: string; showArchivedExpression?: string } = {}): SupportingDocumentGridConfig {
    const parsed = this.buildStructuredConfig(rawConfig, fallbacks);
    const categories = this.normalizeOptions(parsed?.categories);
    const documentTypes = this.normalizeDocumentTypes(parsed?.documentTypes);

    return {
      categories,
      documentTypes,
      requirements: this.normalizeRequirements(parsed?.requirements),
      validation: {
        allowedExtensions: this.normalizeStringArray(parsed?.validation?.allowedExtensions, ['pdf', 'jpg', 'jpeg', 'png']),
        maxFileSizeMb: Number(parsed?.validation?.maxFileSizeMb || 15),
        requireCategory: parsed?.validation?.requireCategory !== false,
        requireType: parsed?.validation?.requireType !== false,
        requireSectionName: !!parsed?.validation?.requireSectionName,
        fileRequiredMessage: parsed?.validation?.fileRequiredMessage || 'Please select a file.',
        categoryRequiredMessage: parsed?.validation?.categoryRequiredMessage || 'Category is required.',
        typeRequiredMessage: parsed?.validation?.typeRequiredMessage || 'Type is required.',
        sectionRequiredMessage: parsed?.validation?.sectionRequiredMessage || '',
        invalidTypeMessage: parsed?.validation?.invalidTypeMessage || '',
        maxSizeMessage: parsed?.validation?.maxSizeMessage || ''
      },
      actions: {
        view: parsed?.actions?.view || '!!row.file_name',
        download: parsed?.actions?.download || '!!row.file_name',
        delete: parsed?.actions?.delete || '!row.isPlaceholder && !readOnly',
        upload: parsed?.actions?.upload || '!readOnly',
        replace: parsed?.actions?.replace || "!!row.file_name && normalizeStatus(row.status) === 'deficient' && !readOnly",
        markValid:  "!!row.file_name && sessionStorage.getItem('role_id') !== '405' && !readOnly && !['Archived', 'Deleted', 'Replaced','Deficient'].includes(row.status) && row.status !== 'Verified'",
        markDeficient: parsed?.actions?.markDeficient || "!!row.file_name && normalizeStatus(row.status) !== 'deficient' && !readOnly",
        markAddressed: parsed?.actions?.markAddressed || "!!row.file_name && ['405','454','455'].includes(sessionStorage.getItem('role_id')) && !readOnly && ['Deficient'].includes(row.status)",
        viewDeficiency: parsed?.actions?.viewDeficiency || "!!row.file_name && normalizeStatus(row.status) === 'deficient'"
      },
      archive: {
        enabledExpression: parsed?.archive?.enabledExpression || fallbacks.showArchivedExpression || '',
        archivedStatuses: this.normalizeStringArray(parsed?.archive?.archivedStatuses, ['archived', 'replaced'])
      },
      section: {
        label: parsed?.section?.label || 'Section Name',
        sourceExpression: parsed?.section?.sourceExpression || ''
      },
      duplicate: {
        matchOn: this.normalizeStringArray(parsed?.duplicate?.matchOn, ['fileName', 'category', 'docType', 'sectionName']),
        allowOverrideExpression: parsed?.duplicate?.allowOverrideExpression || 'false',
        blockMessageStaff: parsed?.duplicate?.blockMessageStaff || 'A matching document already exists.',
        blockMessageApplicant: parsed?.duplicate?.blockMessageApplicant || 'A matching document already exists.'
      },
      api: {
        listEndpoint: parsed?.api?.listEndpoint || '',
        rulesEndpoint: parsed?.api?.rulesEndpoint || '',
        saveEndpoint: parsed?.api?.saveEndpoint || '',
        updateStatusEndpoint: parsed?.api?.updateStatusEndpoint || '',
        deleteEndpoint: parsed?.api?.deleteEndpoint || '',
        replaceEndpoint: parsed?.api?.replaceEndpoint || '',
        syncSaveEndpoint: parsed?.api?.syncSaveEndpoint || '',
        syncDeleteEndpoint: parsed?.api?.syncDeleteEndpoint || ''
      },
      statuses: {
        pendingReview: parsed?.statuses?.pendingReview || 'Pending Review',
        validated: parsed?.statuses?.validated || 'Validated',
        deficient: parsed?.statuses?.deficient || 'Deficient',
        archived: parsed?.statuses?.archived || 'Archived',
        deleted: parsed?.statuses?.deleted || 'Deleted',
        addressed: parsed?.statuses?.addressed || 'Addressed',
        case: parsed?.statuses?.case || 'Case'
      },
      dynamicDocuments: {
        enabled: this.coerceBoolean(parsed?.dynamicDocuments?.enabled),
        sourceGridKey: parsed?.dynamicDocuments?.sourceGridKey || '',
        sourceRecordKeyField: parsed?.dynamicDocuments?.sourceRecordKeyField || '',
        sourceTypeField: parsed?.dynamicDocuments?.sourceTypeField || '',
        sourceTypeLookupRequestType: parsed?.dynamicDocuments?.sourceTypeLookupRequestType || ''
      },
      additionalDynamicType: {
        enabled: this.coerceBoolean(parsed?.additionalDynamicType?.enabled),
        sourceGridKey: parsed?.additionalDynamicType?.sourceGridKey || '',
        sourceFieldName: parsed?.additionalDynamicType?.sourceFieldName || '',
        categoryFieldName: parsed?.additionalDynamicType?.categoryFieldName || ''
      },
      ruleScope: {
        stepperId: parsed?.ruleScope?.stepperId || '',
        pageId: parsed?.ruleScope?.pageId || '',
        panelKey: parsed?.ruleScope?.panelKey || ''
      },
      readOnlyExpression: parsed?.readOnlyExpression || fallbacks.readOnlyExpression || ''
    };
  }

  private buildStructuredConfig(rawConfig: any, fallbacks: { readOnlyExpression?: string; showArchivedExpression?: string }): any {
    const flat = this.toObject(rawConfig);
    const advanced = this.toObject(flat?.configJson);

    const structured = {
      categories: flat?.categoriesConfig || [],
      documentTypes: this.groupDocumentTypes(flat?.documentTypesConfig),
      requirements: flat?.requirementsConfig || [],
      validation: {
        allowedExtensions: flat?.allowedExtensions,
        maxFileSizeMb: flat?.maxFileSizeMb,
        requireCategory: flat?.requireCategory,
        requireType: flat?.requireType,
        requireSectionName: flat?.requireSectionName,
        fileRequiredMessage: flat?.fileRequiredMessage,
        categoryRequiredMessage: flat?.categoryRequiredMessage,
        typeRequiredMessage: flat?.typeRequiredMessage,
        sectionRequiredMessage: flat?.sectionRequiredMessage,
        invalidTypeMessage: flat?.invalidTypeMessage,
        maxSizeMessage: flat?.maxSizeMessage
      },
      actions: {
        view: flat?.viewActionExpression,
        download: flat?.downloadActionExpression,
        delete: flat?.deleteActionExpression,
        upload: flat?.uploadActionExpression,
        replace: flat?.replaceActionExpression,
        markValid: flat?.markValidActionExpression,
        markDeficient: flat?.markDeficientActionExpression,
        markAddressed: flat?.markAddressedActionExpression,
        viewDeficiency: flat?.viewDeficiencyActionExpression
      },
      archive: {
        enabledExpression: flat?.archiveEnabledExpression || flat?.showArchived,
        archivedStatuses: flat?.archivedStatuses
      },
      section: {
        label: flat?.sectionLabel,
        sourceExpression: flat?.sectionSourceExpression
      },
      duplicate: {
        matchOn: flat?.duplicateMatchOn,
        allowOverrideExpression: flat?.duplicateAllowOverrideExpression,
        blockMessageStaff: flat?.duplicateBlockMessageStaff,
        blockMessageApplicant: flat?.duplicateBlockMessageApplicant
      },
      api: {
        listEndpoint: PageBuilderAddURL.EndPoints.supporting_document_rule.getDocumentsForPage,
        rulesEndpoint: PageBuilderAddURL.EndPoints.supporting_document_rule.getAllRulesForStepper,
        saveEndpoint: PageBuilderAddURL.EndPoints.supporting_document_rule.saveDocumentForSection,
        updateStatusEndpoint: PageBuilderAddURL.EndPoints.supporting_document_rule.updateDocumentForSection,
        deleteEndpoint: PageBuilderAddURL.EndPoints.supporting_document_rule.deleteDocumentForSection,
        replaceEndpoint: PageBuilderAddURL.EndPoints.supporting_document_rule.replaceDocumentForSection,
        syncSaveEndpoint: PageBuilderAddURL.EndPoints.supporting_document_rule.syncSaveEndpoint || '',
        syncDeleteEndpoint: PageBuilderAddURL.EndPoints.supporting_document_rule.syncDeleteEndpoint || ''
      },
      statuses: {
        pendingReview: flat?.pendingReviewStatus,
        validated: flat?.validatedStatus,
        deficient: flat?.deficientStatus,
        archived: flat?.archivedStatus,
        deleted: flat?.deletedStatus,
        case: flat?.caseStatus
      },
      dynamicDocuments: {
        enabled: flat?.isDynamicTypeDropdownNeeded,
        sourceGridKey: flat?.dynamicDocumentSourceGridKey,
        sourceRecordKeyField: flat?.dynamicDocumentSourceRecordKeyField,
        sourceTypeField: flat?.dynamicDocumentSourceTypeField,
        sourceTypeLookupRequestType: flat?.dynamicDocumentSourceTypeLookupRequestType
      },
      additionalDynamicType: {
        enabled: flat?.isAdditionalDynamicTypeNeeded,
        sourceGridKey: flat?.additionalDynamicTypeSourceGridKey,
        sourceFieldName: flat?.additionalDynamicTypeSourceFieldName,
        categoryFieldName: flat?.additionalDynamicTypeCategoryFieldName
      },
      ruleScope: {
        stepperId: flat?.stepperId,
        pageId: flat?.pageId,
        panelKey: flat?.panelKey
      },
      readOnlyExpression: flat?.readOnlyCondition || flat?.readOnlyExpression || fallbacks.readOnlyExpression || ''
    };

    return this.deepMerge(structured, advanced);
  }

  private toObject(value: any): any {
    if (!value) {
      return {};
    }
    if (typeof value === 'object') {
      return value;
    }
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }
    return {};
  }

  private normalizeOptions(raw: any): SupportingDocumentGridOption[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((item: any) => {
        if (typeof item === 'string') {
          return { key: item, value: item };
        }
        return {
          key: String(item?.key || item?.value || '').trim(),
          value: String(item?.value || item?.key || '').trim()
        };
      })
      .filter((item: SupportingDocumentGridOption) => !!item.key && !!item.value);
  }

  private normalizeDocumentTypes(raw: any): Record<string, SupportingDocumentGridOption[]> {
    if (!raw || typeof raw !== 'object') {
      return {};
    }
    const normalized: Record<string, SupportingDocumentGridOption[]> = {};
    Object.keys(raw).forEach((key: string) => {
      normalized[key] = this.sortOptions(this.normalizeOptions(raw[key]));
    });
    return normalized;
  }

  private normalizeRequirements(raw: any): any[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((item: any) => ({
      ...item,
      types: this.normalizeStringArray(item?.types, [])
    }));
  }

  private groupDocumentTypes(raw: any): Record<string, SupportingDocumentGridOption[]> {
    if (!Array.isArray(raw)) {
      return {};
    }
    return raw.reduce((acc: Record<string, SupportingDocumentGridOption[]>, item: any) => {
      const category = String(item?.category || '').trim();
      const key = String(item?.key || item?.value || '').trim();
      const value = String(item?.value || item?.key || '').trim();
      if (!category || !key || !value) {
        return acc;
      }
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push({ key, value });
      acc[category] = this.sortOptions(acc[category]);
      return acc;
    }, {});
  }

  private sortOptions(options: SupportingDocumentGridOption[]): SupportingDocumentGridOption[] {
    return [...(options || [])].sort((left: SupportingDocumentGridOption, right: SupportingDocumentGridOption) =>
      String(left?.value || left?.key || '').localeCompare(String(right?.value || right?.key || ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
  }

  private deepMerge(base: any, override: any): any {
    if (!override || typeof override !== 'object') {
      return base;
    }
    if (!base || typeof base !== 'object') {
      return override;
    }

    const result: any = Array.isArray(base) ? [...base] : { ...base };
    Object.keys(override).forEach((key: string) => {
      const baseValue = result[key];
      const overrideValue = override[key];

      if (Array.isArray(overrideValue)) {
        result[key] = overrideValue;
        return;
      }
      if (overrideValue && typeof overrideValue === 'object') {
        result[key] = this.deepMerge(baseValue || {}, overrideValue);
        return;
      }
      result[key] = overrideValue;
    });
    return result;
  }

  private normalizeStringArray(raw: any, fallback: string[]): string[] {
    if (Array.isArray(raw)) {
      return raw.map((item: any) => String(item).trim()).filter(Boolean);
    }
    if (typeof raw === 'string' && raw.trim()) {
      return raw.split(',').map((item: string) => item.trim()).filter(Boolean);
    }
    return fallback;
  }

  private coerceBoolean(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    return ['true', '1', 'yes', 'y'].includes(String(value ?? '').trim().toLowerCase());
  }
}
